import { GameContext } from '../../../contexts/GameContext';
import { GameStat } from '../../../gql-types';
import { ProfileContext } from '../../../contexts/ProfileContext';
import { StopWatchContext } from '../../../contexts/StopWatchContext';
import { ZdarKidsGameContext } from '../../../contexts/ZdarKidsGameContext';
import {
    ZdarKidsGameStepConfiguration,
    zdarKidsGameNumberSteps,
} from '../../../configuration/games/zdar-kids-game/zdarKidsGameContentConfiguration';
import { isEmptyObject } from '../../../util/isEmptyObject';
import { useGraphQL } from '../../../hooks/useGraphQL';
import { useTime } from '../../../hooks/useTime';
import ContinueButton from '../../../components/common/buttons/ContinueButton';
import GameAudioPlayer from '../../../components/common/GameAudioPlayer';
import Loading from '../../../components/common/Loading';
import React, { useContext, useEffect, useState } from 'react';
import ZdarKidsCipher from '../../../components/games/zdar-kids-game/ZdarKidsCipher';
import ZdarKidsCodeInput from '../../../components/games/zdar-kids-game/ZdarKidsCodeInput';
import ZdarKidsEnding from './ZdarKidsEnding';
import ZdarKidsGameToolbar from '../../../components/games/zdar-kids-game/ZdarKidsGameToolbar';
import ZdarKidsHeader from '../../../components/games/zdar-kids-game/ZdarKidsHeader';
import ZdarKidsHints from '../../../components/games/zdar-kids-game/ZdarKidsHints';
import ZdarKidsMap from '../../../components/games/zdar-kids-game/ZdarKidsMap';
import ZdarKidsPointsModal from '../../../components/games/zdar-kids-game/modals/ZdarKidsPointsModal';
import ZdarKidsReport from '../../../components/games/zdar-kids-game/ZdarKidsReport';
import ZdarKidsSolutionModal from '../../../components/games/zdar-kids-game/modals/ZdarKidsSolutionModal';
import ZdarKidsTaskText from '../../../components/games/zdar-kids-game/ZdarKidsTaskText';
import ZdarKidsTaskTitle from '../../../components/games/zdar-kids-game/ZdarKidsTaskTitle';

const DEFAULT_STATS: GameStat = {
    timeSpent: '0',
    hintsTaken: 0,
    mistakes: 0,
    audioPlayed: false,
    codes: [],
    points: 0,
    reportClicked: false,
    mapClicked: false,
};

function ZdarKidsGame() {
    const { startGame, saveProgress, savePenalty, endGame, saveStats } = useGraphQL();
    const [audioOpened, setAudioOpened] = useState(false);
    const [currentStepStats, setCurrentStepStats] = useState(DEFAULT_STATS);
    const [currentStepStartTimestamp, setCurrentStepStartTimestamp] = useState(0);
    const { getTimeSplit } = useTime();
    const [loading, setLoading] = useState(false);
    const [shownToolbar, setShownToolbar] = useState(true);

    const { availableGame } = useContext(ProfileContext);

    const { lastStep, configuration, currentStep, nextStepAction, stepContent, setStepContent, setCurrentStep } =
        useContext(GameContext);
    const { saveHint, wrongAnswer } = useContext(StopWatchContext);
    const { reportOpen, mapOpen, openPointsModal, setSolutionModalEnabled, solutionModalOpen } =
        useContext(ZdarKidsGameContext);

    const {
        title,
        taskText,
        taskPreviewText,
        continueButtonText,
        codeInputText,
        codeInputPassCodeRegexp,
        audio,
        cipher,
        points,
    } = stepContent as ZdarKidsGameStepConfiguration;

    useEffect(() => {
        if (configuration) {
            if (availableGame) {
                setCurrentStep((availableGame.lastStep ?? 0) + 1);
                if (availableGame.lastStep === currentStep) {
                    setLoading(false);
                }
                setStepContent(configuration[(availableGame.lastStep ?? 0) + 1]);
            } else {
                setStepContent(configuration[currentStep]);
            }
        }
    }, [configuration, currentStep, availableGame]);

    useEffect(() => {
        setCurrentStepStats({
            ...DEFAULT_STATS,
            codes: [],
        });
        setCurrentStepStartTimestamp(Math.floor(new Date().getTime() / 1000));
        setAudioOpened(false);
        window.scrollTo(0, 0);
        setShownToolbar(true);
    }, [currentStep]);

    const onOpenAudio = (value: boolean) => {
        setCurrentStepStats({
            ...currentStepStats,
            audioPlayed: true,
        });
        setAudioOpened(value);
    };

    const onFocus = () => {
        !solutionModalOpen && setShownToolbar(false);
    };

    const onFocusOut = () => {
        setShownToolbar(true);
    };

    const onWrongAnswer = (code: string) => {
        const newCodes = currentStepStats.codes;
        newCodes?.push(code);
        setCurrentStepStats({
            ...currentStepStats,
            mistakes: (currentStepStats.mistakes || 0) + 1,
            codes: newCodes,
        });
        wrongAnswer(savePenalty);
    };

    const onOpenMap = () => {
        setCurrentStepStats({
            ...currentStepStats,
            mapClicked: true,
        });
    };

    const onOpenReport = () => {
        setCurrentStepStats({
            ...currentStepStats,
            reportClicked: true,
        });
    };

    const determineIfPointsAwarded = () => {
        if (currentStep === 19) {
            const step15 = availableGame?.stats?.['_15'];
            const step16 = availableGame?.stats?.['_16'];
            const step17 = availableGame?.stats?.['_17'];
            const step18 = availableGame?.stats?.['_18'];
            return points &&
                currentStepStats.hintsTaken === 0 &&
                step15?.hintsTaken === 0 &&
                step16?.hintsTaken === 0 &&
                step17?.hintsTaken === 0 &&
                step18?.hintsTaken === 0
                ? 10
                : 0;
        } else if (currentStep === 29) {
            const step26 = availableGame?.stats?.['_26'];
            const step27 = availableGame?.stats?.['_27'];
            const step28 = availableGame?.stats?.['_28'];
            return points &&
                currentStepStats.hintsTaken === 0 &&
                step26?.hintsTaken === 0 &&
                step27?.hintsTaken === 0 &&
                step28?.hintsTaken === 0
                ? 10
                : 0;
        }
        return points && currentStepStats.hintsTaken === 0 ? 10 : 0;
    };

    const getHintsTakenForModal = () => {
        if (currentStep === 19) {
            const step15 = availableGame?.stats?.['_15'];
            const step16 = availableGame?.stats?.['_16'];
            const step17 = availableGame?.stats?.['_17'];
            const step18 = availableGame?.stats?.['_18'];
            return (
                (currentStepStats.hintsTaken || 0) +
                (step15?.hintsTaken || 0) +
                (step16?.hintsTaken || 0) +
                (step17?.hintsTaken || 0) +
                (step18?.hintsTaken || 0)
            );
        } else if (currentStep === 29) {
            const step26 = availableGame?.stats?.['_26'];
            const step27 = availableGame?.stats?.['_27'];
            const step28 = availableGame?.stats?.['_28'];
            return (
                (currentStepStats.hintsTaken || 0) +
                (step26?.hintsTaken || 0) +
                (step27?.hintsTaken || 0) +
                (step28?.hintsTaken || 0)
            );
        }
        return currentStepStats.hintsTaken;
    };

    const onConfirm = () => {
        setStepContent({ id: 1000 });
        setLoading(true);
        if (currentStep > 1) {
            const end = Math.floor(new Date().getTime() / 1000);
            const { getSeconds, getMinutes, getHours } = getTimeSplit(end - currentStepStartTimestamp);
            saveProgress(
                currentStep,
                {
                    ...currentStepStats,
                    timeSpent: getHours + ':' + getMinutes + ':' + getSeconds,
                    points: determineIfPointsAwarded(),
                },
                availableGame?.token ?? null,
            );
            setSolutionModalEnabled(true);
            nextStepAction({});
        }
        if (currentStep === 1) {
            nextStepAction({ availableGame, startGame });
        }
        if (currentStep === lastStep) {
            const end = Math.floor(new Date().getTime() / 1000);
            const { getSeconds, getMinutes, getHours } = getTimeSplit(end - currentStepStartTimestamp);
            saveProgress(
                currentStep,
                {
                    ...currentStepStats,
                    timeSpent: getHours + ':' + getMinutes + ':' + getSeconds,
                },
                availableGame?.token ?? null,
            );
            nextStepAction({ availableGame, endGame });
        }
    };
    const takeHint = () => {
        setCurrentStepStats({
            ...currentStepStats,
            hintsTaken: (currentStepStats.hintsTaken || 0) + 1,
        });
        saveHint(savePenalty);
        saveStats(
            currentStep,
            { ...currentStepStats, hintsTaken: (currentStepStats.hintsTaken || 0) + 1 },
            availableGame?.token ?? null,
        );
    };
    if (currentStep === 36 || availableGame?.finalTime !== null) {
        return (
            <React.Fragment>
                <div className="zdar-kids-game">
                    <div className="zdar-kids-game-wrapper">
                        <ZdarKidsHeader />
                        <ZdarKidsEnding title={title} currentStep={currentStep} />
                    </div>
                </div>
            </React.Fragment>
        );
    }

    if (isEmptyObject(stepContent) && currentStep < 36) {
        return (
            <React.Fragment>
                <div className="zdar-kids-game">
                    <div className="zdar-kids-game-wrapper">
                        <ZdarKidsHeader />
                        <div className="card game-card">
                            <Loading color="#23ab70" />
                        </div>
                    </div>
                </div>
            </React.Fragment>
        );
    }

    if (availableGame && currentStep < 36) {
        const lastStep = availableGame.lastStep;
        if (lastStep === null || (lastStep && currentStep < lastStep)) {
            return (
                <React.Fragment>
                    <div className="zdar-kids-game">
                        <div className="zdar-kids-game-wrapper">
                            <ZdarKidsHeader />
                            <div className="card game-card">
                                <Loading color="#23ab70" />
                            </div>
                        </div>
                    </div>
                </React.Fragment>
            );
        }
    }

    if (loading && currentStep < 36) {
        return (
            <React.Fragment>
                <div className="zdar-kids-game">
                    <div className="zdar-kids-game-wrapper">
                        <ZdarKidsHeader />
                        <div className="card game-card">
                            <Loading color="#23ab70" />
                        </div>
                    </div>
                </div>
            </React.Fragment>
        );
    }
    return (
        <React.Fragment>
            <div className="zdar-kids-game">
                <div className="zdar-kids-game-wrapper">
                    {currentStep > 1 && <ZdarKidsHeader />}
                    <div className="panel">
                        <div className="card game-card">
                            {reportOpen && <ZdarKidsReport currentStep={currentStep} />}
                            {mapOpen && <ZdarKidsMap currentStep={currentStep} />}
                            {!reportOpen && !mapOpen && (
                                <React.Fragment>
                                    {title && (
                                        <ZdarKidsTaskTitle
                                            text={title}
                                            audioOpened={audioOpened}
                                            setAudioOpened={onOpenAudio}
                                            audio={audio}
                                        />
                                    )}
                                    {audio && <GameAudioPlayer track={audio} opened={audioOpened} />}
                                    {taskText && (
                                        <ZdarKidsTaskText
                                            text={taskText}
                                            title={title}
                                            previewText={taskPreviewText}
                                            audio={audio}
                                            audioOpened={audioOpened}
                                            setAudioOpened={onOpenAudio}
                                            currentStep={currentStep}
                                        />
                                    )}
                                    <ZdarKidsHints />
                                    <ZdarKidsCipher onSuccess={points ? () => openPointsModal() : onConfirm} />
                                    {codeInputText && (
                                        <ZdarKidsCodeInput
                                            text={codeInputText}
                                            passCodeRegexp={codeInputPassCodeRegexp}
                                            onConfirm={points ? () => openPointsModal() : onConfirm}
                                            isNumber={zdarKidsGameNumberSteps.includes(currentStep)}
                                            wrongAnswer={onWrongAnswer}
                                            onFocus={onFocus}
                                            onFocusOut={onFocusOut}
                                        />
                                    )}
                                    {continueButtonText && !cipher?.custom && (
                                        <ContinueButton
                                            text={continueButtonText}
                                            onClick={points ? () => openPointsModal() : onConfirm}
                                        />
                                    )}
                                </React.Fragment>
                            )}
                        </div>
                        {currentStep > 1 && shownToolbar && (
                            <div>
                                <div className="fake-menu" />
                                <div className="menu-wrapper">
                                    <ZdarKidsGameToolbar
                                        onOpenMap={onOpenMap}
                                        onOpenReport={onOpenReport}
                                        closeAudio={() => setAudioOpened(false)}
                                    />
                                </div>
                            </div>
                        )}

                        <ZdarKidsSolutionModal takeHint={takeHint} />
                        {/*// @ts-expect-error*/}
                        <ZdarKidsPointsModal fallback={onConfirm} hintsTaken={getHintsTakenForModal()} />
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
}

export default ZdarKidsGame;
